import React, { ForwardedRef, forwardRef } from 'react';
import './button.css';
import Loader from '../loader/Loader';

interface Props {
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
	color?:
		| 'primary'
		| 'secondary'
		| 'error'
		| 'warning'
		| 'success'
		| 'grey'
		| string;
	variant?: 'solid' | 'text' | 'outlined';
	size?: 'small' | 'medium' | 'large';
	loading?: boolean;
	iconStart?: string;
	iconEnd?: string;
	style?: React.CSSProperties;
	className?: string;
	[key: string]: any;
}

export default forwardRef<HTMLButtonElement, Props>(function Button(
	props: Props,
	ref: ForwardedRef<HTMLButtonElement>
) {
	// Destructure all known props and leave other props to be cascaded down to the root element
	const {
		onClick,
		disabled,
		color,
		variant,
		size,
		loading,
		iconStart,
		iconEnd,
		className,
		...otherProps
	} = props;

	const isLoading = loading ?? false;

	const {
		classes: rootClasses = [],
		style = {},
		...remainingProps
	} = otherProps || {};

	const loaderPixelSize = size === 'small' ? 18 : 24;

	const isIconOnly =
		React.Children.count(props.children) === 0 && (iconStart || iconEnd);

	// Resolve custom color if it isn't one of the theme colors
	const customColor = ![
		'primary',
		'secondary',
		'error',
		'warning',
		'success',
		'grey'
	].includes(color)
		? color
		: undefined;

	const styles = {
		color: customColor,
		...style
	};

	return (
		<button
			ref={ref}
			onClick={(e) => {
				if (isLoading || disabled) return;

				onClick?.(e);
			}}
			disabled={disabled}
			className={
				[
					'ds_button',
					...(!customColor
						? [`ds_button__${props.color ?? 'primary'}`]
						: []),
					...(isLoading ? ['ds_button__loading'] : []),
					...(variant === 'outlined'
						? ['ds_button__variant_outlined']
						: []),
					...(variant === 'text' ? ['ds_button__variant_text'] : []),
					...(size === 'small' ? ['ds_button__size_small'] : []),
					...(size === 'medium' ? ['ds_button__size_medium'] : []),
					...(size === 'large' || !size
						? ['ds_button__size_large']
						: []),
					...(isIconOnly ? ['ds_button__icon_button'] : []),
					...rootClasses
				].join(' ') + (!!className ? ` ${className}` : '')
			}
			style={styles || undefined}
			{...remainingProps}
		>
			<div className="ds_button__content">
				{isLoading && (
					<div className="ds_button__loader">
						<Loader
							size={loaderPixelSize}
							variant={variant}
							color={
								color as
									| 'primary'
									| 'secondary'
									| 'error'
									| 'warning'
									| 'success'
									| 'grey'
							}
						/>
					</div>
				)}
				<div
					className={isLoading ? 'invisible' : ''}
					style={{
						display: 'flex',
						gap: '10px',
						alignItems: 'center'
					}}
				>
					{iconStart && <i className={`${iconStart}`} />}
					{props.children}
					{iconEnd && <i className={`${iconEnd}`} />}
				</div>
			</div>
		</button>
	);
});
